import {
  ILearnDownloadableItem,
  ILearnDownloadableTask,
  lessonSelectors
} from 'common/modules/ReactShared';
import { t } from 'i18n';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTypedSelector } from 'students/stores/_utils';
import { customMediaQuery } from 'students/views/shared/styled';
import { getAccessibleColor } from 'students/views/shared/utils/colorUtils';
import styled from 'styled-components';
import { motion as m } from 'framer-motion';

import { LearnTaskControls } from '../../components';
import { ILearnTaskProps } from '../types';

interface IDownloadableItemProps {
  item: ILearnDownloadableItem;
  downloaded: boolean;
  onDownload: (itemId: number) => void;
  accessibleColor: string;
}

const DownloadableItem: FC<IDownloadableItemProps> = ({
  item,
  downloaded,
  onDownload,
  accessibleColor
}) => {
  const handleDownload = useCallback(() => {
    try {
      // Validate URL before opening
      const url = new URL(item.fileURL);
      // Allow only safe protocols to be opened in a new tab
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        window.open(url.href, '_blank', 'noopener,noreferrer');
        onDownload(item.id);
      }
    } catch {
      // URL is invalid - silently fail
    }
  }, [item.fileURL, item.id, onDownload]);

  const displayName = item.fileName || item.fileURL.split('/').pop() || 'Download';

  return (
    <SDownloadItem downloaded={downloaded} accessibleColor={accessibleColor}>
      <SItemContent>
        <SFileName>{displayName}</SFileName>
      </SItemContent>
      <SDownloadButton
        onClick={handleDownload}
        downloaded={downloaded}
        accessibleColor={accessibleColor}
        aria-label={
          downloaded
            ? t('frontend.lesson_task.tasks.learn.downloadable.downloaded_file', {
                fileName: displayName,
                defaultValue: `Downloaded file ${displayName}`
              })
            : t('frontend.lesson_task.tasks.learn.downloadable.download_file', {
                fileName: displayName,
                defaultValue: `Download file ${displayName}`
              })
        }
      >
        {downloaded ? (
          <>
            <SCheckIcon aria-hidden="true">✓</SCheckIcon>
            {t('frontend.lesson_task.tasks.learn.downloadable.downloaded', {
              defaultValue: 'Downloaded'
            })}
          </>
        ) : (
          <>
            <SDownloadIcon aria-hidden="true">↓</SDownloadIcon>
            {t('frontend.lesson_task.tasks.learn.downloadable.download', {
              defaultValue: 'Download'
            })}
          </>
        )}
      </SDownloadButton>
    </SDownloadItem>
  );
};

const LearnDownloadableTask: FC<ILearnTaskProps<ILearnDownloadableTask>> = ({
  task,
  onFinish,
  lightFont,
  isCompleting,
  previousTaskId
}) => {
  const { title, items, introduction } = task;

  const [downloadedItems, setDownloadedItems] = useState<Set<number>>(new Set());
  const lesson = useTypedSelector(lessonSelectors.selectLesson);
  const themeColor = lesson?.color || null;

  // Calculate an accessible color variant that meets WCAG AA contrast requirements
  const accessibleColor = useMemo(
    () => (themeColor ? getAccessibleColor(themeColor) : '#1976D2'),
    [themeColor]
  );

  const handleDownload = useCallback((itemId: number) => {
    setDownloadedItems((prev) => new Set(prev).add(itemId));
  }, []);

  return (
    <STaskWrapper>
      <STitle light={lightFont}>{title}</STitle>

      {introduction && (
        <SIntroduction
          light={lightFont}
          dangerouslySetInnerHTML={{ __html: introduction }}
        />
      )}

      <SDownloadList
        role="list"
        aria-label={t('frontend.lesson_task.tasks.learn.downloadable.files_list', {
          defaultValue: 'Downloadable files'
        })}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        {items.map((item) => (
          <DownloadableItem
            key={item.id}
            item={item}
            downloaded={downloadedItems.has(item.id)}
            onDownload={handleDownload}
            accessibleColor={accessibleColor}
          />
        ))}
      </SDownloadList>

      <SProgressInfo light={lightFont} aria-live="polite">
        {t('frontend.lesson_task.tasks.learn.downloadable.progress', {
          downloaded: downloadedItems.size,
          total: items.length,
          defaultValue: `${downloadedItems.size} of ${items.length} files downloaded`
        })}
      </SProgressInfo>

      <LearnTaskControls
        onFinish={onFinish}
        isCompleting={isCompleting}
        previousTaskId={previousTaskId}
      />
    </STaskWrapper>
  );
};

export default LearnDownloadableTask;

// Styled Components
const STaskWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  padding: 0 1rem;

  ${customMediaQuery('tablet')} {
    max-width: 800px;
    align-self: center;
    width: 100%;
  }
`;

const STitle = styled.h1<{ light: boolean }>`
  color: ${({ light }) =>
    light ? 'var(--lingu-font-light-color)' : 'var(--lingu-font-dark-color)'};
  background-color: transparent !important;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  text-align: center;

  ${customMediaQuery('tablet')} {
    font-size: 1.75rem;
  }
`;

const SIntroduction = styled.div<{ light: boolean }>`
  color: ${({ light }) =>
    light ? 'var(--lingu-font-light-color)' : 'var(--lingu-font-dark-color)'};
  background-color: transparent !important;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const SDownloadList = styled(m.div)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SDownloadItem = styled.div<{ downloaded: boolean; accessibleColor: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: ${({ downloaded }) =>
    downloaded ? 'rgba(56, 142, 60, 0.1)' : 'rgba(255, 255, 255, 0.9)'};
  border-radius: 12px;
  border: 2px solid
    ${({ downloaded, accessibleColor }) => {
      if (downloaded) return '#388E3C';
      return accessibleColor;
    }};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:focus-within {
    outline: 2px solid ${({ accessibleColor }) => accessibleColor};
    outline-offset: 2px;
  }
`;

const SItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const SFileName = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SDownloadButton = styled.button<{
  downloaded: boolean;
  accessibleColor: string;
}>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease, opacity 0.2s ease, transform 0.2s ease;
  margin-left: 1rem;
  flex-shrink: 0;
  background: ${({ downloaded, accessibleColor }) =>
    downloaded ? '#388E3C' : accessibleColor};
  color: white;

  &:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus {
    outline: 2px solid
      ${({ downloaded, accessibleColor }) => (downloaded ? '#388E3C' : accessibleColor)};
    outline-offset: 2px;
  }
`;

const SDownloadIcon = styled.span`
  font-size: 1.125rem;
  font-weight: bold;
`;

const SCheckIcon = styled.span`
  font-size: 1rem;
`;

const SProgressInfo = styled.div<{ light: boolean }>`
  text-align: center;
  font-size: 0.875rem;
  color: ${({ light }) => (light ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.7)')};
  background-color: transparent !important;
  margin-bottom: 1.5rem;
`;
